import { observer } from "mobx-react-lite";

import {
  ButtonsGroupChoice,
  FontIcon,
  Stack,
  Text,
  useTheme
} from "@bps/fluent-ui";
import { QuestionnaireDto } from "@libs/gateways/clinical/ClinicalGateway.dtos.ts";
import { ButtonsGroupSingleChoiceField } from "@ui-components/form/ButtonsGroupSingleChoiceField.tsx";

import { ClinicalToolInformationPanel } from "../shared-components/ClinicalToolInformationPanel.tsx";
import {
  ClinicalToolAnswer,
  findClinicalToolAnswer
} from "../utils/ClinicalToolAnswerFinder.ts";
import { getQuestionnaireAnswerTexts } from "../utils/clinicalToolsUtils.ts";

const SIZE_FULL_WIDTH = 950;
const SIZE_SEMI_FULL = 720;
const SIZE_SHORT = 530;

interface EPDSQuestionnaireFormProps {
  questionnaire: QuestionnaireDto;
  answers?: ClinicalToolAnswer[];
  isViewOnly?: boolean;
  width: number;
  questionPrefix?: string;
}

export const EPDSQuestions: React.FC<EPDSQuestionnaireFormProps> = observer(
  ({ questionnaire, answers, isViewOnly, width, questionPrefix }) => {
    const theme = useTheme();

    const getBgColour = (index: number) => {
      return index % 2 === 0
        ? theme.palette.neutralLighterAlt
        : theme.palette.white;
    };

    const findAnswer = (questionId: string): string => {
      let answerValue = "";
      if (answers) {
        const answer = findClinicalToolAnswer(
          isViewOnly ? questionId : `${questionPrefix}${questionId}`,
          answers
        );
        if (answer && answer.answerValue) answerValue = answer.answerValue;
      }
      return answerValue;
    };

    const isVerticalAlignment = width < SIZE_FULL_WIDTH;
    const isShortWidth = SIZE_SHORT < width && width <= SIZE_SEMI_FULL;

    const questions = questionnaire.items;

    return (
      <Stack>
        <ClinicalToolInformationPanel
          title="Edinburgh Postnatal Depression Scale"
          onRenderOpenTooltip={() => <>References</>}
          onRenderCloseTooltip={() => (
            <>
              Close. Click the{" "}
              <FontIcon
                iconName="Info"
                styles={{ root: { color: theme.palette.accent } }}
              />{" "}
              icon to show again
            </>
          )}
          onRenderContent={() => (
            <Stack horizontal>
              <FontIcon
                iconName="Info"
                styles={{ root: { paddingRight: 8 } }}
              />
              <Text variant="small">
                <b>Reference:</b> Cox, J.L., Holden, J.M., and Sagovsky, R.
                1987. Detection of postnatal depression: Development of the
                10-item Edinburgh Postnatal Depression Scale. British Journal of
                Psychiatry 150:782-786.
              </Text>
            </Stack>
          )}
        />

        {questions.map((item, index) => {
          const isQuestionSix = item.id === 6; // We want question 6 to always be verticle, as per the design of the figma.

          return (
            <Stack
              key={item.id.toString()}
              horizontal
              verticalAlign="center"
              tokens={{ childrenGap: 8 }}
              styles={{
                root: {
                  backgroundColor: getBgColour(index),
                  padding: "8px 8px"
                }
              }}
            >
              <Stack
                horizontalAlign="space-between"
                verticalAlign="center"
                tokens={{ childrenGap: 8 }}
                styles={{
                  root: {
                    backgroundColor: getBgColour(index),
                    paddingTop: 8,
                    paddingBottom: 8,
                    paddingRight: 8,
                    width: "100%"
                  }
                }}
              >
                <Stack
                  horizontal
                  styles={{
                    root: { maxWidth: isVerticalAlignment ? "100%" : "50%" }
                  }}
                >
                  <Text bold styles={{ root: { minWidth: 16 } }}>
                    {item.id}.
                  </Text>
                  <Text bold styles={{ root: { paddingLeft: 18 } }}>
                    {item.text}
                  </Text>
                </Stack>
                <Stack>
                  {!answers && (
                    <ButtonsGroupSingleChoiceField
                      name={`q${item.id}`}
                      options={getQuestionnaireAnswerTexts(
                        item.answerOptions || [],
                        {
                          disabled: false
                        }
                      )}
                      vertical={isQuestionSix || isShortWidth}
                      fieldItemStyles={{
                        item: { marginLeft: 33 }
                      }}
                    />
                  )}

                  {answers && (
                    <ButtonsGroupChoice
                      name={`q${item.id}`}
                      options={getQuestionnaireAnswerTexts(
                        item.answerOptions || [],
                        {
                          disabled: true
                        }
                      )}
                      value={findAnswer(`${item.id}`)}
                      styles={{
                        root: {
                          marginLeft: 40,
                          backgroundColor: getBgColour(index),
                          width: "fit-content"
                        }
                      }}
                      vertical={isQuestionSix || isShortWidth}
                      onChange={() => {}}
                    />
                  )}
                </Stack>
              </Stack>
            </Stack>
          );
        })}
      </Stack>
    );
  }
);
